'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { MessageCircle, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/lib/i18n';

export default function AuthCTABanner() {
  const { user } = useAuth();
  const router = useRouter();
  const { t, isRTL } = useLanguage();

  const handleAskQuestion = (e: React.MouseEvent) => {
    e.preventDefault();
    if (user) {
      router.push('/ai-chat-interface');
    } else {
      router.push('/sign-up-login-screen');
    }
  };

  return (
    <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 pb-16" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white px-8 py-12 md:px-12 md:py-16">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
          <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-accent-500/15 blur-2xl" />
        </div>

        <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
              <MessageCircle size={26} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles size={14} className="text-accent-400" />
                <span className="text-sm font-medium text-accent-300">{t.cta.badge}</span>
              </div>
              <h3 className="font-jakarta text-2xl md:text-3xl font-700 text-white mb-2">
                {t.cta.title}
              </h3>
              <p className="text-white/65 text-sm leading-relaxed max-w-xl">
                {t.cta.description}
              </p>
              <div className="flex flex-wrap gap-3 mt-3">
                {t.cta.features?.map((feature) => (
                  <span
                    key={`feature-${feature}`}
                    className="inline-flex items-center gap-1 text-xs text-white/70 before:content-['✓'] before:text-green-400 before:font-bold before:mr-0.5"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={handleAskQuestion}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-medium text-sm bg-accent-500 hover:bg-accent-600 text-white transition-all duration-150 active:scale-95 shadow-lg shadow-accent-500/30 whitespace-nowrap"
            >
              {t.cta.askButton}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
            {!user && (
              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-medium text-sm bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-all duration-150 active:scale-95 whitespace-nowrap"
              >
                {t.cta.loginButton}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}